<template>
  <el-form
    ref="form"
    :model="opportunity"
    label-width="auto"
    size="small"
  >
    <el-form-item
      label="Address"
      :show-message="!opportunity.account.$fields.address.valid"
      :error="opportunity.account.$fields.address.message"
      required
    >
      <el-input
        v-model="opportunity.account.address"
        clearable
      />
    </el-form-item>

    <el-row>
      <el-col :span="8">
        <el-form-item
          label="Suburb"
          :show-message="!opportunity.account.$fields.suburb.valid"
          :error="opportunity.account.$fields.suburb.message"
          required
        >
          <el-autocomplete
            v-model="opportunity.account.suburb"
            placement="top"
            clearable
            :fetch-suggestions="findSuburb"
            :trigger-on-focus="false"
            :debounce="500"
            @select="handleSuburbSelection"
          >
            <template v-slot="{ item }">
              {{ `${item.suburb}, ${item.state}, ${item.postcode}` }}
            </template>
          </el-autocomplete>
        </el-form-item>
      </el-col>

      <el-col :span="8">
        <el-form-item
          label="State"
          :show-message="!opportunity.account.$fields.state.valid"
          :error="opportunity.account.$fields.state.message"
          required
        >
          <el-input
            v-model="opportunity.account.state"
            clearable
          />
        </el-form-item>
      </el-col>

      <el-col :span="8">
        <el-form-item
          label="Postcode"
          :show-message="!opportunity.account.$fields.postcode.valid"
          :error="opportunity.account.$fields.postcode.message"
          required
        >
          <el-input
            v-model="opportunity.account.postcode"
            clearable
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item
          label="Contact"
          :show-message="!opportunity.account.$fields.contact.valid"
          :error="opportunity.account.$fields.contact.message"
          required
        >
          <el-input
            v-model="opportunity.account.contact"
            clearable
          />
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Phone"
          :show-message="!opportunity.account.$fields.phone.valid"
          :error="opportunity.account.$fields.phone.message"
          required
        >
          <el-input
            v-model="opportunity.account.phone"
            clearable
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item
          label="Fax"
          :show-message="!opportunity.account.$fields.fax.valid"
          :error="opportunity.account.$fields.fax.message"
        >
          <el-input
            v-model="opportunity.account.fax"
            clearable
          />
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Email"
          :show-message="!opportunity.account.$fields.email.valid"
          :error="opportunity.account.$fields.email.message"
        >
          <el-input
            v-model="opportunity.account.email"
            clearable
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row>
      <el-col>
        <el-form-item
          label="Portfolio"
          prop="portfolio"
        >
          <el-select
            v-model="opportunity.account.portfolio"
            :loading="isLoadingPortfolios"
            value-key="id"
            clearable
            class="w-100"
          >
            <el-option
              v-for="portfolio in portfolios"
              :key="portfolio.id"
              :label="`${portfolio.long || '-'}`"
              :value="portfolio"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-row>
          <el-col>
            <el-form-item
              :show-message="!opportunity.$fields.worth.valid"
              :error="opportunity.$fields.worth.message"
              required
            >
              <template #label>
                Annual Spend ($)
              </template>

              <el-input-number
                v-model="opportunity.worth"
                :precision="0"
                :step="10000"
                :min="0"
                step-strictly
                class="w-100"
              />
            </el-form-item>
          </el-col>

          <el-col>
            <el-form-item
              label="Incumbent Carriers"
              :show-message="!opportunity.$fields.incumbent_carriers.valid"
              :error="opportunity.$fields.incumbent_carriers.message"
              required
            >
              <el-select
                v-model="incumbentCarriers"
                multiple
                filterable
                remote
                value-key="id"
                allow-create
                default-first-option
                :remote-method="findCarrier"
                :loading="isLoadingCarriers"
                class="w-100"
              >
                <el-option
                  v-for="carrier in carriers"
                  :key="carrier.id"
                  :label="carrier.name"
                  :value="carrier"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Daily Volume"
          :show-message="!opportunity.$fields.volume.valid"
          :error="opportunity.$fields.volume.message"
          required
        >
          <el-input
            v-model="opportunity.volume"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 4}"
            clearable
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item
          label="Suggested Carriers"
          :show-message="!opportunity.$fields.suggested_carriers.valid"
          :error="opportunity.$fields.suggested_carriers.message"
        >
          <el-select
            v-model="suggestedCarriers"
            multiple
            filterable
            remote
            value-key="id"
            default-first-option
            :remote-method="findCarrier"
            :loading="isLoadingCarriers"
            class="w-100"
          >
            <el-option
              v-for="carrier in carriers"
              :key="carrier.id"
              :label="carrier.name"
              :value="carrier"
            />
          </el-select>
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item
          label="Freight Types"
          :show-message="!opportunity.$fields.freights.valid"
          :error="opportunity.$fields.freights.message"
          required
        >
          <el-select
            v-model="selectedItemTypes"
            multiple
            value-key="id"
            :loading="isLoadingItemTypes"
            class="w-100"
          >
            <el-option
              v-for="type in itemTypes"
              :key="type.id"
              :label="type.desc"
              :value="type"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <el-form-item
      label="Comments"
      :show-message="!opportunity.$fields.reason.valid"
      :error="opportunity.$fields.reason.message"
      required
    >
      <el-input
        v-model="opportunity.reason"
        type="textarea"
        :autosize="{ minRows: 6, maxRows: 8}"
        clearable
      />
    </el-form-item>
  </el-form>
</template>

<script>
import OpportunityCreateForm from '@/components/Application/Sales/Opportunities/Opportunity/Form/Create'
import AccountLead from '@/plugins/sdk/resources/Account/Lead'

export default {
  extends: OpportunityCreateForm,
  props: {
    opportunity: {
      type: Object,
      required: true,
      validator: function (value) {
        return value instanceof AccountLead
      }
    }
  },
  data () {
    return {
      portfolios: [],
      isLoadingPortfolios: true
    }
  },
  created () {
    this
      .$sdk
      .dictionary_portfolio
      .list(builder => {
        builder.limit = 1000
      })
      .then(res => { this.portfolios = res })
      .finally(() => { this.isLoadingPortfolios = false })
  }
}
</script>
